import { Controller } from "@hotwired/stimulus";
import { undecorateClone } from "../../utils/g4l_select";

// La saisie des questions — carte #1594 lot G, **réécrit le 2026-09-20 pour les
// HUIT types** (plan `docs/go4learn/PLAN_CREATION_QUIZ_ET_RESSOURCES.md`, §6).
//
// Il fait quatre gestes et pas un cinquième : ajouter une question **du type
// demandé**, en retirer une, ajouter une ligne à une collection (un choix, un
// élément, une paire, un trou), en retirer une. Le reste — les barèmes, la
// validation, les six contrôles — vit côté serveur, dans `QuestionFormMapper`,
// le registre de types et `SubmissionChecks`.
//
// 🔴 **Ce qui a disparu : l'hydratation.** Il relisait les questions déjà
// saisies depuis un `<script type="application/json">` et les réinjectait champ
// par champ. Avec un seul type et deux champs, c'était tenable ; avec huit types
// et leurs collections, c'eût été une SECONDE écriture de chaque champ — et deux
// écritures d'un même champ divergent toujours. Les questions existantes sont
// désormais rendues en ERB par `entry.html.erb`, avec le MÊME partial que les
// gabarits. Le contrôleur ne les remplit plus : il les renumérote, comme les
// autres.
//
// 🔴 **Pourquoi les index sont RENUMÉROTÉS après chaque geste.** Les six
// contrôles nomment les questions par leur numéro : « Questions 5 et 7 »,
// « Question 3 : énoncé de 41 mots ». Si retirer la question 2 laissait des
// champs `questions[2]` et `questions[4]`, le serveur trierait sur ces clés et
// l'auteur recevrait des numéros qui ne correspondent à rien de ce qu'il voit.
// Un contrôle qui nomme la mauvaise question est pire qu'un contrôle qui compte.
export default class extends Controller {
  static targets = ["list", "template", "count", "navigator", "pointsTotal", "pointsMeter", "pointsHint", "navItem",
    "regenerate"];
  // Le seuil du contrôle ⑥ (`SubmissionChecks::MAX_STATEMENT_WORDS`), servi par la vue : le
  // compte en direct du prototype (« 41 mots ») doit dire la MÊME chose que l'étape 4.
  static values = { maxWords: { type: Number, default: 35 }, startEmpty: { type: Boolean, default: false },
                    scale: { type: Number, default: 0 }, target: { type: Number, default: 0 },
                    navLabel: { type: String, default: "%{n} · %{type} · %{points} pts" },
                    remaining: { type: String, default: "" }, complete: { type: String, default: "" } };

  // Le type d'une question neuve quand rien ne le précise — le seul que tout
  // enseignant a déjà écrit.
  static DEFAULT_TYPE = "single_choice";

  connect() {
    // Un dépôt neuf commence avec une question : un formulaire vide n'indique
    // pas ce qu'on attend, et le corpus ouvre l'étape 2 sur « Question 1 sur 8 ».
    // Except in AI mode: step 2 reviews the generated questions, and a blank one would
    // invite the author to type the brief into a statement (owner's report, 2026-10-18).
    if (!this.startEmptyValue && this.listTarget.querySelectorAll("[data-question]").length === 0) {
      this.addQuestion();
    }
    this.#renumber();
    this.#select(0);
  }

  // ⚠️ Le type arrive en `data-depot-questions-type-param` sur le bouton. Un type
  // sans gabarit ne fait RIEN plutôt que d'insérer une question vide de sens :
  // l'auteur verrait un bloc sans champ de réponse et croirait le formulaire cassé.
  addQuestion(event) {
    const asked = event?.params?.type || this.constructor.DEFAULT_TYPE;
    const template = this.templateTargets.find((node) => node.dataset.type === asked);
    if (!template) return;

    this.listTarget.insertAdjacentHTML("beforeend", template.innerHTML);
    this.#renumber();
    this.#select(this.#questions().length - 1);
    const added = this.listTarget.lastElementChild;
    if (added) {
      // ⚠️ **Appel OPTIONNEL** : `scrollIntoView` n'existe pas dans un DOM de test
      // (jsdom), et une garde qui tomberait ici ne mesurerait plus que son harnais.
      added.scrollIntoView?.({ block: "center", behavior: "smooth" });
      added.querySelector("textarea")?.focus();
    }
  }

  removeQuestion(event) {
    const block = event.target.closest("[data-question]");
    if (!block) return;

    // ⚠️ La dernière question ne se retire pas : un quiz sans question n'est pas
    // un brouillon, c'est un formulaire cassé. Le retrait se refuse en silence
    // plutôt que d'afficher une erreur — l'auteur voit que le bloc reste.
    if (this.listTarget.querySelectorAll("[data-question]").length <= 1) return;

    const index = this.#questions().indexOf(block);
    block.remove();
    this.#renumber();
    this.#dropServerPositions();
    this.#select(Math.max(0, index - 1));
  }

  // 🔴 **Une ligne neuve est un CLONE de la dernière, vidée.** Un gabarit par
  // collection et par type ferait huit gabarits de plus à tenir synchronisés avec
  // les lignes rendues — et le jour où l'un prendrait du retard, l'auteur
  // obtiendrait une ligne qui ne poste pas les mêmes champs que ses voisines,
  // sans rien voir. Le clone, lui, ne peut pas diverger de ce qu'il copie.
  // Design Q2 (2026-10-19): one question in the editor at a time, chosen in the list.
  show(event) {
    this.#select(Number(event.currentTarget.dataset.index));
  }

  moveUp(event) {
    const block = event.target.closest("[data-question]");
    const questions = this.#questions();
    const index = questions.indexOf(block);
    if (index <= 0) return;
    this.#keepingAnswers(() => {
      questions[index - 1].before(block);
      this.#renumber();
    });
    this.#dropServerPositions();
    this.#select(index - 1);
  }

  duplicate(event) {
    const block = event.target.closest("[data-question]");
    if (!block) return;
    const copy = block.cloneNode(true);
    // `cloneNode` copies attributes, not what the author typed: the values follow by hand.
    const from = block.querySelectorAll("input, textarea, select");
    copy.querySelectorAll("input, textarea, select").forEach((field, i) => {
      if (field.type === "radio" || field.type === "checkbox") field.checked = from[i].checked;
      else field.value = from[i].value;
    });
    undecorateClone(copy);
    this.#keepingAnswers(() => {
      block.after(copy);
      this.#renumber();
    }, copy);
    this.#dropServerPositions();
    this.#select(this.#questions().indexOf(copy));
  }

  // 🔴 A question's correct answer is a radio group named `questions[N][correct]`. While a
  // question is moved or copied, two questions briefly share a name, and the browser unticks
  // the other one's answer (HTML spec: one checked radio per group) — the quiz was saved
  // with a question that had no correct answer (review of PR #1873). The ticked radios are
  // noted before the change and ticked again once every name is unique.
  #keepingAnswers(change, extra = null) {
    const ticked = [
      ...this.listTarget.querySelectorAll("input[type=radio]:checked"),
      ...(extra ? extra.querySelectorAll("input[type=radio]:checked") : []),
    ];
    change();
    ticked.forEach((radio) => { radio.checked = true; });
  }

  // 🔴 A « Régénérer » link names a question by its SAVED position. Once the order changes
  // on the page and is not saved yet, that position names another question — and the PATCH
  // would also throw away the unsaved order (review of PR #1873). The links go until the
  // page is saved and drawn again.
  #dropServerPositions() {
    this.regenerateTargets.forEach((link) => link.remove());
  }

  tally() {
    this.#renumber();
  }

  addRow(event) {
    const zone = event.target.closest("[data-rows]");
    const rows = zone?.querySelectorAll("[data-row]");
    if (!rows?.length) return;

    const last = rows[rows.length - 1];
    const clone = last.cloneNode(true);
    this.#blank(clone);
    undecorateClone(clone);
    last.after(clone);
    this.#renumber();
    clone.querySelector("input[type=text], textarea")?.focus();
  }

  // ⚠️ **Le plancher est DÉCLARÉ par la zone** (`data-min-rows`), pas deviné ici :
  // un QCM exige deux choix (le registre le refuse en dessous), un texte à trous
  // en exige un. Un plancher écrit dans le JavaScript serait une seconde
  // expression de la règle du registre, et c'est celle-là qu'on oublierait.
  removeRow(event) {
    const zone = event.target.closest("[data-rows]");
    const row = event.target.closest("[data-row]");
    if (!zone || !row) return;

    const floor = Number(zone.dataset.minRows || 1);
    if (zone.querySelectorAll("[data-row]").length <= floor) return;

    row.remove();
    this.#renumber();
  }

  // Appelé à chaque frappe dans un énoncé (`input->depot-questions#words`).
  words(event) {
    const block = event.target.closest("[data-question]");
    if (block) this.#countWords(block);
  }

  // Vide un clone de ligne : aucune valeur de la ligne copiée ne doit survivre —
  // ni le texte, ni la coche de bonne réponse, ni le média désigné.
  #blank(row) {
    row.querySelectorAll("input, textarea, select").forEach((field) => {
      if (field.type === "radio" || field.type === "checkbox") {
        field.checked = false;
      } else if (field.tagName === "SELECT") {
        field.selectedIndex = 0;
      } else {
        field.value = "";
      }
    });
  }

  // Réécrit tous les `name` sur les index réels du DOM, et les libellés visibles
  // avec eux. Les deux doivent bouger ENSEMBLE : c'est la correspondance entre
  // « Question 3 » à l'écran et `questions[2]` au serveur qui fait que le
  // message du contrôle ⑥ désigne la bonne ligne.
  #questions() {
    return Array.from(this.listTarget.querySelectorAll("[data-question]"));
  }

  // Shows the chosen question and what hangs from it (its « régénérer » link), hides the others.
  #select(index) {
    this.selectedIndex = index;
    let current = -1;
    Array.from(this.listTarget.children).forEach((child) => {
      if (child.hasAttribute("data-question")) current += 1;
      // `display`, not `hidden`: `.g4l-stack` sets `display` at author level and beats `[hidden]`.
      // A row marked `data-depot-questions-keep` (the restore link) belongs to the whole list.
      const kept = child.hasAttribute("data-depot-questions-keep");
      child.style.display = kept || current === index || current < 0 ? "" : "none";
    });
    this.navItemTargets.forEach((item) => this.#markCurrent(item, Number(item.dataset.index) === index));
  }

  // The open question reads as chosen, in the colours `.g4l-choice` takes when checked.
  #markCurrent(item, current) {
    item.setAttribute("aria-current", current ? "true" : "false");
    item.style.borderColor = current ? "var(--g4l-accent)" : "";
    item.style.background = current ? "var(--g4l-accent-tint)" : "";
  }

  #points(question) {
    return Number(question.querySelector("[name$='[points]']")?.value || 0);
  }

  #drawNavigator(questions) {
    if (!this.hasNavigatorTarget) return;
    this.navigatorTarget.replaceChildren(...questions.map((question, index) => {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "g4l-choice";
      item.style.textAlign = "left";
      item.dataset.index = String(index);
      item.dataset.depotQuestionsTarget = "navItem";
      item.dataset.action = "depot-questions#show";
      item.textContent = this.navLabelValue
        .replace("%{n}", String(index + 1))
        .replace("%{type}", question.querySelector("[data-testid=question-type]")?.textContent?.trim() || "")
        .replace("%{points}", String(this.#points(question)));
      this.#markCurrent(item, index === this.selectedIndex);
      return item;
    }));
  }

  // « Points répartis : 18 / 20 » — the total cannot exceed the scale.
  #drawPoints(questions) {
    const total = questions.reduce((sum, question) => sum + this.#points(question), 0);
    if (this.hasPointsTotalTarget) this.pointsTotalTarget.textContent = String(total);
    if (this.hasPointsMeterTarget && this.scaleValue > 0) {
      this.pointsMeterTarget.style.width = `${Math.min(100, Math.round((total / this.scaleValue) * 100))}%`;
    }
    if (this.hasPointsHintTarget && this.scaleValue > 0) {
      const toWrite = Math.max(0, this.targetValue - questions.length);
      // Every point shared out: say it, rather than « 0 point(s) for 0 question(s) ».
      if (Math.abs(total - this.scaleValue) < 0.005 && this.completeValue) {
        this.pointsHintTarget.textContent = this.completeValue;
        return;
      }
      this.pointsHintTarget.textContent = this.remainingValue
        .replace("%{points}", String(this.scaleValue - total))
        .replace("%{questions}", String(toWrite));
    }
  }

  #renumber() {
    const questions = this.listTarget.querySelectorAll("[data-question]");

    questions.forEach((question, qIndex) => {
      question.querySelectorAll("[data-label-num]").forEach((label) => {
        label.textContent = String(qIndex + 1);
      });

      question.querySelectorAll("[name]").forEach((field) => {
        field.name = field.name.replace(/^questions\[\d*\]/, `questions[${qIndex}]`);
      });

      question.querySelectorAll("[data-rows]").forEach((zone) => this.#renumberRows(zone));
    });

    if (this.hasCountTarget) this.countTarget.textContent = String(questions.length);
    questions.forEach((question) => this.#countWords(question));
    this.#drawNavigator(Array.from(questions));
    this.#drawPoints(Array.from(questions));
  }

  // 🔴 **Les clés des choix sont POSITIONNELLES (`c1`, `c2`…), et c'est un contrat.**
  //
  // `Go4learn::Quiz::TypedQuestion#choice_key_at` traduit l'index posté par les
  // deux players (web et mobile) en clé de choix, par la position. Des clés
  // aléatoires ou conservées après un retrait de ligne feraient qu'un apprenant
  // qui touche la 2ᵉ réponse verrait sa réponse rapportée à une AUTRE.
  //
  // ⚠️ La marque de bonne réponse (radio ou case) porte la MÊME valeur : c'est
  // elle que le serveur lit comme `correct` / `correct_keys[]`. La réécrire ici,
  // au même endroit et au même moment que la clé, est ce qui garantit qu'elles ne
  // peuvent pas se désaccorder.
  #renumberRows(zone) {
    const collection = zone.dataset.rows;
    const keyPrefix = zone.dataset.rowKeyPrefix;
    const pattern = new RegExp(`\\[${collection}\\]\\[\\d*\\]`);

    zone.querySelectorAll("[data-row]").forEach((row, rIndex) => {
      row.querySelectorAll("[name]").forEach((field) => {
        field.name = field.name.replace(pattern, `[${collection}][${rIndex}]`);
      });
      row.querySelectorAll("[data-row-num]").forEach((num) => {
        num.textContent = String(rIndex + 1);
      });

      const key = row.querySelector("[data-choice-key]");
      if (key) {
        key.value = `c${rIndex + 1}`;
        const mark = row.querySelector("[data-correct]");
        if (mark) mark.value = key.value;
      }

      // ⚠️ La clé d'un trou est PROPOSÉE, jamais imposée : elle doit correspondre
      // au marqueur du texte (`[b1]`), et un auteur qui nomme ses trous `nom` et
      // `verbe` doit pouvoir le faire. On ne remplit donc que le vide.
      const rowKey = row.querySelector("[data-row-key]");
      if (rowKey && keyPrefix && !rowKey.value.trim()) rowKey.value = `${keyPrefix}${rIndex + 1}`;
    });
  }

  // 🔴 **La même règle que le serveur** : `text.squish.split.size`. Au-delà du seuil, la
  // pastille passe en alerte ET la phrase qui le dit apparaît — jamais la seule couleur.
  #countWords(block) {
    const field = block.querySelector("[name$='[statement]']");
    const pill = block.querySelector("[data-word-count]");
    if (!field || !pill) return;
    const words = field.value.trim().split(/\s+/).filter(Boolean).length;
    const over = words > this.maxWordsValue;
    const template = words <= 1 ? pill.dataset.templateOne : pill.dataset.template;
    pill.textContent = template.replace("%{count}", String(words));
    pill.classList.toggle("g4l-chip--alerte", over);
    pill.classList.toggle("g4l-chip--info", !over);
    const hint = block.querySelector("[data-word-alert]");
    if (hint) hint.hidden = !over;
  }
}
